import Image from "next/image";
import Link from "next/link";
import mentorsData from "@/data/mentors.json"; // Import the mentors JSON data

export default function ProgramHero({ program }) {
  if (!program) {
    return null;
  }

  // Handle external links safely
  const isExternalLink = (url) => {
    return url && (url.startsWith("http://") || url.startsWith("https://"));
  };

  // Safe rendering of links to avoid issues
  const renderSafeLink = (url, className, children) => {
    if (!url)
      return (
        <button className={className} disabled>
          {children}
        </button>
      );

    if (isExternalLink(url)) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        >
          {children}
        </a>
      );
    }

    return (
      <Link href={url} className={className}>
        {children}
      </Link>
    );
  };
  
  // Find mentors for this program from mentorsData
  const programMentors = mentorsData.find(item => item.slug === program.slug);
  const mentors = [];
  
  // If we found mentor data, extract it
  if (programMentors) {
    // Extract mentor information
    for (let i = 1; i <= 11; i++) {
      const nameKey = `mentor${i}name`;
      const imageKey = `mentor${i}image`;
      const postKey = `mentor${i}post`;
      
      if (programMentors[nameKey] && programMentors[imageKey] && programMentors[postKey]) {
        mentors.push({
          name: programMentors[nameKey],
          image: programMentors[imageKey],
          position: programMentors[postKey]
        });
      }
    }
  }
  
  // Number of mentors to display
  const visibleCount = 2;
  
  // Calculate additional mentors
  const additionalMentors = Math.max(0, mentors.length - visibleCount);

  return (
    <div className="w-full flex items-center justify-center mx-auto py-10 md:py-16 mt-20">
      <div className="w-[90%] lg:[80%] md:w-[90%] text-white bg-[#142f2e] p-8 md:p-10 rounded-[24px]">
        {/* Desktop Layout */}
        <div className="hidden md:block">
          <div className="flex flex-col md:flex-row">
            {/* Left Column - Image */}
            <div className="md:w-[55%] pr-12">
              <div
                className="relative w-auto rounded-2xl overflow-hidden"
                style={{ height: "350px" }}
              >
                <Image
                  src={program.mainImage}
                  alt={program.heading}
                  fill
                  className="object-cover"
                  priority
                />
              </div>

              {/* Action Buttons */}
              <div className="flex mt-6 gap-4">
                {renderSafeLink(
                  program.applyLink || "#",
                  "bg-[#33c375] hover:bg-[#54b556] text-[#002726] text-xl font-medium px-6 py-4 rounded-lg flex-1 text-center transition-colors",
                  "Apply Now"
                )}

                {renderSafeLink(
                  program.brochureLink || "#",
                  "bg-[#fffaf1] hover:bg-gray-100 text-xl text-[#002726] font-medium px-6 py-4 rounded-lg flex-1 text-center transition-colors flex items-center justify-center",
                  <>
                   <Image
                  src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/dwar.svg"
                  alt="Brochure Icon"
                  width={20}
                  height={20}
                  className="mr-2"
                  priority
                />
                    Brochure
                  </>
                )}
              </div>
            </div>

            {/* Right Column - Content */}
            <div className="md:w-[45%] flex flex-col">
              <div>
                <h1 className="text-[28px] md:text-[40px] font-medium mb-4">
                  {program.heading}
                </h1>
                <p className="text-sm md:text-base font-normal mb-10">
                  {program.subheading}
                </p>

                <div className="mb-12">
                  <p className="text-gray-300 text-xs md:text-sm font-normal">
                    Cohort start date
                  </p>
                  <p className="text-base font-medium">{program.date}</p>
                </div>
              </div>

              {/* Mentors Section - Moved to bottom with flex-grow to push it down */}
              <div className="mt-auto">
                <p className="mb-4">Mentors</p>
                <div className="flex items-center flex-nowrap">
                  {mentors.length > 0 ? (
                    <>
                      <div className="flex items-center">
                        {/* Display visible mentors */}
                        {mentors.slice(0, visibleCount).map((mentor, index) => (
                          <div key={index} className="flex items-center mr-6">
                            <div className="relative w-10 h-10 rounded-lg overflow-hidden border-2 border-[#1b3c3a] flex-shrink-0">
                              <Image
                                src={mentor.image}
                                alt={mentor.name}
                                fill
                                className="object-cover"
                              />
                            </div>
                            <div className="ml-3">
                              <p className="font-medium text-base truncate max-w-[120px]">{mentor.name}</p>
                              <p className="text-gray-300 text-sm">{mentor.position}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Show "+X more" if there are additional mentors */}
                      {additionalMentors > 0 && (
                        <div className="text-white font-medium text-base">
                          + {additionalMentors} more
                        </div>
                      )}
                    </>
                  ) : (
                    // Fallback to legacy format if no mentors found in mentorsData
                    program.mentorImage && (
                      <div className="flex items-center">
                        <div className="relative w-10 h-10 rounded-lg overflow-hidden border-2 border-[#1b3c3a] flex-shrink-0">
                          <Image
                            src={program.mentorImage}
                            alt={program.name}
                            fill
                            className="object-cover"
                          />
                        </div>
                        <div className="ml-3">
                          <p className="font-medium text-base truncate max-w-[120px]">
                            {program.name.includes("Multiple")
                              ? program.name.replace("Multiple mentors from", "")
                              : program.name}
                          </p>
                          <p className="text-gray-300 text-sm">{program.designation}</p>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          {/* Image Section */}
          <div
            className="relative w-full rounded-2xl overflow-hidden mb-6"
            style={{ height: "170px" }}
          >
            <Image
              src={program.mainImage}
              alt={program.heading}
              fill
              className="object-cover"
              priority
            />
          </div>

          {/* Mentor Section - Mobile */}
          <div className="mb-8">
            <p className="mb-3">Mentors</p>
            <div className="flex items-center">
              {mentors.length > 0 ? (
                <>
                  {/* Display first mentor */}
                  <div className="flex items-center mr-3">
                    <div className="relative w-10 h-10 rounded-lg overflow-hidden border-2 border-[#1b3c3a] flex-shrink-0">
                      <Image
                        src={mentors[0].image}
                        alt={mentors[0].name}
                        fill
                        className="object-cover"
                      />
                    </div>
                    <div className="ml-2">
                      <p className="font-medium text-sm truncate max-w-[100px]">{mentors[0].name}</p>
                      <p className="text-gray-300 text-xs truncate max-w-[100px]">{mentors[0].position}</p>
                    </div>
                  </div>
                  
                  {/* Show "+X more" with all additional mentors (including second one) */}
                  {mentors.length > 1 && (
                    <div className="text-white font-medium text-sm">
                      + {mentors.length - 1} more
                    </div>
                  )}
                </>
              ) : (
                // Fallback to legacy format
                program.mentorImage && (
                  <div className="flex items-center">
                    <div className="relative w-10 h-10 rounded-lg overflow-hidden border-2 border-[#1b3c3a] flex-shrink-0">
                      <Image
                        src={program.mentorImage}
                        alt={program.name}
                        fill
                        className="object-cover"
                      />
                    </div>
                    <div className="ml-2">
                      <p className="font-medium text-sm truncate max-w-[100px]">
                        {program.name.includes("Multiple")
                          ? program.name.replace("Multiple mentors from", "")
                          : program.name}
                      </p>
                      <p className="text-gray-300 text-xs truncate max-w-[100px]">{program.designation}</p>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Program Title */}
          <h1 className="text-[28px] font-medium mb-4">{program.heading}</h1>
          <p className="text-sm md:text-base mb-8">{program.subheading}</p>

          {/* Cohort Date */}
          <div className="mb-8">
            <p className="text-gray-300 text-xs">Cohort start date</p>
            <p className="text-base font-medium">{program.date}</p>
          </div>

          {/* Buttons - Mobile */}
          <div className="flex flex-col gap-4 mt-6">
            {renderSafeLink(
              program.applyLink || "#",
              "bg-[#33c375] hover:bg-[#54b556] text-white text-base font-medium py-4 rounded-lg w-full text-center transition-colors",
              "Apply Now"
            )}

            {renderSafeLink(
              program.brochureLink || "#",
              "bg-white hover:bg-gray-100 text-[#142f2e] text-base font-medium py-4 rounded-lg w-full text-center transition-colors flex items-center justify-center",
              <>
                <Image
                  src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/dwar.svg"
                  alt="Brochure Icon"
                  width={20}
                  height={20}
                  className="mr-2"
                  priority
                />
                Brochure
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}